import React from 'react';
import Button from '../../common/Button';
import StatusBadge from '../../common/StatusBadge';
import { Text, Typography } from '../../common/Typography';
import ClubLogo from '../../clubs/ClubLogo';
import { formatSessionActivityMetaLine, getSessionStatusLabel } from './sessionHubUtils';

const getStatusVariant = (status) => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'upcoming':
      return 'primary';
    case 'ongoing':
      return 'success';
    case 'completed':
      return 'warning';
    case 'archived':
      return 'default';
    case 'cancelled':
      return 'danger';
    default:
      return 'primary';
  }
};

const getPastSessionAdminBadge = (session, completedSessionAllPlayersMarked) => {
  if (session.session_status === 'ongoing') {
    return {
      text: 'Live',
      className: 'bg-success-light text-success-dark border-success/30',
    };
  }
  if (session.session_status === 'completed') {
    if (completedSessionAllPlayersMarked[session.id] === false) {
      return {
        text: 'Not archived',
        className: 'bg-warning-light text-warning-dark border-warning/30',
      };
    }
    if (completedSessionAllPlayersMarked[session.id]) {
      return {
        text: 'Ready to archive',
        className: 'bg-info-light text-info-dark border-info/30',
      };
    }
  }
  return null;
};

const getPastSessionRowClass = (session) => {
  if (session.session_status === 'ongoing') {
    return 'border-success/40 bg-success-light/40 border-l-4 border-l-success shadow-brand-sm';
  }
  return 'border-border-medium bg-surface-subtle';
};

const SessionPastMobileCard = ({
  session,
  clubLogoUrl,
  clubName,
  completedSessionAllPlayersMarked,
  taskSummary,
  onEnterSession,
  onReviewAndArchive,
  onArchive,
  onDelete,
  canDelete,
  hasPaymentRecords,
}) => {
  const adminBadge = getPastSessionAdminBadge(session, completedSessionAllPlayersMarked);
  const showActivityStats =
    session.session_status === 'ongoing' || session.session_status === 'completed';

  return (
    <div className={`flex flex-col gap-3 rounded-xl border p-3 ${getPastSessionRowClass(session)}`}>
      <div className="flex min-w-0 items-start gap-3">
        <ClubLogo
          logoImageUrl={clubLogoUrl}
          clubName={clubName}
          size="sm"
          className="border border-border-medium shadow-brand-sm mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {session.session_status === 'ongoing' ? (
              <span
                className="inline-flex h-2 w-2 shrink-0 rounded-full bg-success animate-pulse"
                aria-hidden
              />
            ) : null}
            <Typography as="span" variant="strong" className="break-words">
              {session.name}
            </Typography>
            {adminBadge ? (
              <span
                className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium border ${adminBadge.className}`}
              >
                {adminBadge.text}
              </span>
            ) : (
              <StatusBadge variant={getStatusVariant(session.session_status)} size="small">
                {getSessionStatusLabel(session.session_status)}
              </StatusBadge>
            )}
            {adminBadge &&
            session.session_status !== 'archived' &&
            session.session_status !== 'cancelled' ? (
              <span className="text-xs text-text-muted">
                {getSessionStatusLabel(session.session_status)}
              </span>
            ) : null}
          </div>
          <Text className="text-xs text-text-secondary mt-1">
            {formatSessionActivityMetaLine(session, { showActivityStats })}
          </Text>
          {session.session_types?.name ? (
            <Text className="text-xs text-text-muted mt-0.5">{session.session_types.name}</Text>
          ) : null}
          {taskSummary &&
          session.session_status !== 'upcoming' &&
          session.session_status !== 'draft' ? (
            <div className="mt-2">{taskSummary}</div>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        {session.session_status === 'ongoing' ? (
          <Button
            type="button"
            variant="primary"
            size="small"
            className="w-full justify-center sm:w-auto"
            onClick={() => onEnterSession(session.id)}
          >
            Enter session
          </Button>
        ) : session.session_status === 'completed' ? (
          <Button
            type="button"
            variant="primary"
            size="small"
            className="w-full justify-center sm:w-auto"
            onClick={() => onReviewAndArchive(session.id)}
          >
            Review & archive
          </Button>
        ) : session.session_status === 'archived' ? (
          <Button
            type="button"
            variant="outline"
            size="small"
            className="w-full justify-center sm:w-auto"
            onClick={() => onReviewAndArchive(session.id)}
          >
            View session
          </Button>
        ) : null}
        {session.session_status === 'completed' && completedSessionAllPlayersMarked[session.id] ? (
          <Button
            type="button"
            variant="outline"
            size="small"
            className="w-full justify-center sm:w-auto"
            onClick={() => onArchive(session.id)}
          >
            Archive session
          </Button>
        ) : null}
        {canDelete && session.session_status === 'completed' ? (
          hasPaymentRecords ? (
            <Text className="text-xs text-text-muted">
              Delete unavailable — session has payment records.
            </Text>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="small"
              className="w-full justify-center border-danger text-danger hover:bg-danger-light sm:w-auto"
              onClick={() => onDelete(session)}
            >
              Delete session
            </Button>
          )
        ) : session.session_status === 'completed' ? (
          <Text className="text-xs text-text-muted">Delete only when no games were played.</Text>
        ) : null}
      </div>
    </div>
  );
};

export default SessionPastMobileCard;
